export const STRINGS = {
  accountStatus: {
    // Same wording as the server's USER_RESPONSE.ACCOUNT_BLOCKED / ACCOUNT_DELETED, which the login screen matches on.
    blocked: 'This account has been blocked. Please contact support.',
    deleted: 'This account has been deleted.',
  },
  adminActions: {
    blocked: 'Blocked',
    confirm: 'Confirm',
    cancel: 'Cancel',
    actions: 'Actions',
    user: {
      block: 'Block user',
      blocked: 'User blocked',
      unblocked: 'User unblocked',
      deleted: 'User deleted',
      blockTitle: 'Block this user?',
      unblockTitle: 'Unblock this user?',
      deleteTitle: 'Delete this user?',
      blockHint: 'Signs them out, stops login',
      deleteHint: 'Removes them and their projects',
      unblock: 'Unblock user',
      delete: 'Delete user',
      confirmBlock: 'Block this user? They are signed out at once and cannot log in until unblocked.',
      confirmUnblock: 'Unblock this user? They can log in again.',
      confirmDelete: 'Delete this user and all of their projects? This cannot be undone from the admin screens.',
      updateFailed: 'Could not update the user. Please try again.',
      deleteFailed: 'Could not delete the user. Please try again.',
    },
    project: {
      block: 'Block project',
      blocked: 'Project blocked',
      unblocked: 'Project unblocked',
      deleted: 'Project deleted',
      blockTitle: 'Block this project?',
      unblockTitle: 'Unblock this project?',
      deleteTitle: 'Delete this project?',
      blockHint: 'Stops AR loading and owner edits',
      deleteHint: 'Removes the project',
      unblock: 'Unblock project',
      delete: 'Delete project',
      confirmBlock: 'Block this project? The owner cannot change it and the AR experience stops loading until it is unblocked.',
      confirmUnblock: 'Unblock this project? It goes back to exactly how it was.',
      confirmDelete: 'Delete this project? This cannot be undone from the admin screens.',
      updateFailed: 'Could not update the project. Please try again.',
      deleteFailed: 'Could not delete the project. Please try again.',
    },
  },
  zoomPill: {
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    resetZoom: 'Reset zoom to 100%',
  },
  enterOtp: {
    title: 'Check your email',
    sentTo: 'We sent a 6-digit code to',
    verify: 'Verify code',
    back: 'Back',
    resend: 'Resend code',
    resendIn: 'Resend in',
    didntGet: "Didn't get it?",
    resent: 'A new code is on its way',
    invalid: 'Invalid OTP. Please try again.',
  },
  navBar: {
    backToProjects: 'Back to projects',
    projects: 'Projects',
    unsavedChanges: {
      title: 'You have unsaved changes',
      message: 'Save your project before leaving. If you go back without saving, your changes will be lost.',
      save: 'Save and go back',
      discard: 'Discard changes and go back',
    },
    profilePopup: {
      pricing: 'Pricing',
      accountSettings: 'Account settings',
      support: 'Support',
      logout: 'Logout',
    },
  },
  brand: {
    name: 'PicPop Studio',
    tagline: 'Turn Pictures into Web AR',
    logoAlt: 'PicPop Studio logo',
    welcome: 'Welcome to PicPop Studio.',
  },
  ar: {
    previewsLabel: 'Free previews',
    previewsOfLeft: (max: number): string => `of ${max} left`,
    previewsLeft: (remaining: number, max: number): string => `${remaining} of ${max} free previews left`,
    lockedAlt: 'Locked',
    restrictedTitle: 'Free previews used up',
    restrictedDesc: 'Please contact the creator to unlock this experience.',
    noLinkButton: "This button isn't linked to anything yet.",
    noLinkSocial: "This icon isn't linked to anything yet.",
    noLinkHint: 'Set the link in the project.',
    loading: 'Loading your experience…',
    errors: {
      retry: 'Try again',
      generic: { title: 'Something went wrong', message: "We couldn't load this experience. Check your connection and try again." },
      notFound: { title: 'Experience not found', message: 'This link may be wrong, or the project was removed. Ask the creator for a new link.' },
      cameraDenied: { title: 'Camera access is needed', message: 'Allow camera access for this site in your browser settings, then try again.' },
      unsupported: { title: "This browser can't run AR", message: 'Open this link in Chrome or Safari on your phone.' },
    },
  },
  chooseObject: {
    title: 'Add an object',
    subtitle: "Pick what appears on top of your target when it's scanned.",
    closeAlt: 'close',
    image: { name: 'Image', description: 'JPG, PNG or WebP' },
    text: { name: 'Text', description: 'Headline or label' },
    button: { name: 'Button', description: 'Opens a link' },
    video: { name: 'Video', description: 'Plays on target' },
    social: { name: 'Social', description: 'Links to your social profiles' },
  },
  supportScreen: {
    title: 'Support',
    subtitle: 'Track your tickets or start a new one. Replies usually arrive within a day.',
    newTicket: 'New ticket',
    searchPlaceholder: 'Search tickets',
    statusPlaceholder: 'Status',
    statusAll: 'All statuses',
    statusOpen: 'Open',
    statusInProgress: 'In progress',
    statusResolved: 'Resolved',
    statusClosed: 'Closed',
    colTicket: 'Ticket',
    colSubject: 'Subject',
    colStatus: 'Status',
    colOpened: 'Opened',
    emptyMessage: 'No support tickets found',
    createTitle: 'New support ticket',
    createSubtitle: 'Tell us what went wrong. We usually reply within a day.',
    subjectLabel: 'Subject',
    subjectPlaceholder: 'Subject',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Describe your issue',
    attachmentLabel: 'Attach a screenshot (optional)',
    cancel: 'Cancel',
    submit: 'Submit ticket',
    close: 'Close',
    back: 'Back to projects',
    loading: 'Loading...',
    attachmentAlt: 'Ticket attachment',
    previewAlt: 'Selected attachment preview',
    attachmentLoading: 'Loading attachment...',
    replies: 'Replies',
    noReplies: 'No replies yet',
    replySupport: 'Support',
    replyYou: 'You',
    requiredError: 'Subject and description are required',
    submitted: 'Ticket submitted',
    submitFailed: 'Failed to submit ticket',
    loadFailed: 'Failed to load ticket',
    attachmentSizeError: 'Attachment must be 10MB or smaller',
    attachmentTypeError: 'Only image attachments are allowed',
  },
  accountSettings: {
    title: 'Account settings',
    backToProjects: 'Back to projects',
    backToOverview: 'Back to overview',
    profileSubtitle: 'How you appear in PicPop Studio.',
    emailHelp: 'You sign in with this email.',
    support: {
      title: 'Support',
      subtitle: 'Track your tickets or start a new one. Our team usually replies within a day.',
      emptyTitle: 'No tickets yet',
      emptyText: 'Stuck on something or have a question? Open a ticket and your conversation will show up here.',
      newTicket: 'New ticket',
      subject: 'Subject',
      subjectPlaceholder: 'A short summary of the problem',
      description: 'Describe your issue',
      descriptionPlaceholder: 'What happened, and what did you expect to happen?',
      attachmentLabel: 'Screenshot (optional)',
      attachmentEmpty: 'JPG, PNG or WebP, up to 10 MB.',
      uploadImage: 'Upload image',
      replaceImage: 'Replace image',
      back: 'Back',
      submit: 'Submit ticket',
    },
    photoHint: 'JPG or PNG. A square image works best.',
    unsavedChangesTitle: 'You have unsaved changes',
    unsavedChangesMessage: 'If you leave now, they will be lost.',
    discardChanges: 'Discard changes',
    save: 'Save',
    close: 'Close',
    myBills: {
      title: 'Plan & bills',
      subtitle: 'Your payment history and invoices.',
      loading: 'Loading invoices…',
      empty: 'No invoices yet',
      error: 'Failed to load invoices.',
      loadMore: 'Load more',
      paid: 'Paid',
      columns: {
        date: 'Date',
        invoiceNumber: 'Invoice no.',
        net: 'Net',
        gst: 'GST',
        total: 'Total',
        status: 'Status',
      },
    },
  },
  editor: {
    undo: 'Undo',
    redo: 'Redo',
    emptyState: {
      title: 'Add your target image',
      description: 'This is the picture people scan with their phone, like a poster, label or business card. Everything else sits on top of it.',
      laterTargetTitle: 'Add an image for this target',
      laterTargetDescription: 'Each target is a separate picture people can scan. The objects you place on this one appear only when it is scanned.',
      tileIconAlt: 'Target image',
      addTargetTitle: 'Start with one image',
      addTargetSubtitle: 'Choose it from your computer',
      addButton: 'Add target image',
      requirementFormats: 'JPG, PNG or WebP',
      requirementMaxSizePrefix: 'Up to',
      requirementMaxSizeUnit: 'MB',
      requirementMinSizePrefix: 'At least',
      requirementMinSizeSeparator: '×',
      requirementMinSizeUnit: 'px',
      requirementIconAlt: 'Included',
    },
    properties: {
      eyebrow: 'Properties',
      noSelectionHint: 'Settings for whatever you select will show here.',
      selected: 'Selected',
      selectedObject: 'Selected object',
      targetTitle: 'Target image',
      renameLabel: 'Name',
      renameEmpty: 'Enter a name',
      renameLink: 'Rename',
      renameSave: 'Save',
      renameTaken: 'Another item here already has that name.',
      transform: 'Transform',
      reset: 'Reset',
      position: 'Position',
      rotation: 'Rotation',
      scale: 'Scale',
      keepProportions: 'Keep proportions',
      width: 'Width',
      height: 'Height',
      widthShort: 'W',
      heightShort: 'H',
      unitPx: 'px',
      unitDegree: '°',
      targetHint: 'This is what the camera looks for. Sharp, high-contrast artwork tracks best.',
    },
    propertiesEmptyState: {
      eyebrow: 'Properties',
      hint: 'Settings for whatever you select will show here.',
      tipsTitle: 'What makes a good target',
      goodTips: ['Sharp, high-contrast artwork', 'Lots of detail spread across the image'],
      badTip: 'Avoid plain backgrounds or repeating patterns',
      goodIconAlt: 'Good',
      badIconAlt: 'Avoid',
    },
    sidebar: {
      targetImageTitle: 'Target image',
      objectsTitle: 'Objects',
      targetKind: 'Target',
      addTargetImage: 'Add target image',
      addTargetImageHint: 'Required to start',
      addObjectLabel: 'Add object',
      addTargetLabel: 'Add another target image',
      noObjectsTitle: 'No objects yet',
      noObjectsText: 'Images, text, buttons and videos you add will show here.',
      needTargetFirst: 'Add a target image first. Then you can place images, text, buttons and videos on it.',
      reorderHint: 'Drag to reorder. Objects higher in the list sit in front.',
      deleteIconAlt: 'Delete icon',
      dragIconAlt: 'Drag icon',
    },
    nextStep: {
      title: 'Make this picture pop',
      description: 'Add an image, text, button or video. It appears on top of the target when someone scans it.',
      addObject: 'Add object',
    },
    canvasNote: 'The target is shown faded so your objects stand out.',
    transformToolbar: {
      label: 'Transform tools',
      position: 'Position',
      rotation: 'Rotate',
      scale: 'Scale',
    },
  },
  preview: {
    breadcrumbProjects: 'Projects',
    breadcrumbPreview: 'Preview',
    backToEditor: 'Back to editor',
    title: 'Test it on your phone',
    subtitle: 'Scan, point, and check everything looks right before you publish.',
    statusDraft: 'Draft · not published',
    statusLive: 'Published',
    stepOpenTitle: 'Open it on your phone',
    stepOpenCaption: 'Scan with your phone’s camera',
    qrAlt: 'QR code for this project',
    orCopyLink: 'or copy the link',
    copyLink: 'Copy',
    linkCopied: 'Link copied to clipboard',
    linkCopyFailed: 'Could not copy the link. Please try again.',
    stepPointTitle: 'Point it at your target',
    stepPointHint: 'Keep this image on screen or print it. When your phone finds it, your objects pop up on top.',
    targetImageAlt: 'Target image',
    previousTarget: 'Previous target image',
    nextTarget: 'Next target image',
    noTargetImage: 'No target image yet',
    publishTitle: 'Publish when it looks right',
    publishDescription: 'Publishing makes the experience live for everyone who scans your target.',
    publishButton: 'Publish project',
    publishedTitle: 'Your project is live',
    publishedDescription: 'Everyone who scans your target sees your objects pop up.',
    filesTitle: 'Files for print and sharing',
    filesDescription: 'Put them on posters, packaging or anywhere people will scan.',
    downloadQr: 'Download QR code',
    qrDownloadFailed: 'Could not download the QR code. Please try again.',
    filesLockedHint: 'Images unlock after you publish.',
    lockIconAlt: 'Locked',
    downloadImages: {
      button: 'Download images',
      lockedHint: 'Available after the project is published.',
      success: 'Images downloaded',
      failed: 'Could not download the images. Please try again.',
    },
  },
  modal: {
    closeAlt: 'close',
  },
  upgradePlan: {
    back: 'Back',
    title: 'Simple pricing, pay per target',
    subtitle: 'Pay for each target image you publish. No subscriptions, no tiers.',
    planName: 'Per target',
    planDescription: 'For every poster, label or campaign you bring to life with AR.',
    unit: '/ target image',
    gstNote: 'Includes 18% GST',
    validity: 'Live for 2 years',
    includesHeading: 'Each target includes',
    tickAlt: 'Included',
    features: [
      'The PicPop editor and image tracking',
      'Image, text, button, video and social objects',
      'A shareable QR code and viewer link',
      'Download your target image, ready to print',
      'Renew whenever your access ends',
      'GST invoice emailed after payment',
    ],
    footerPrefix: 'Questions about pricing? ',
    footerLink: 'Ask our team',
  },
  priceQuote: {
    title: 'Review and pay',
    descriptionPrefix: 'Publishing ',
    descriptionSuffix: ' makes it live for everyone who scans its targets.',
    descriptionNoName: 'Publishing makes your project live for everyone who scans its targets.',
    targetsHeading: (count: number, eachPrice: string | null): string =>
      `${count} target ${count === 1 ? 'image' : 'images'}${eachPrice ? ` · ${eachPrice} each` : ''}`,
    targetLabel: (position: number, name?: string): string => name?.trim() ? name : `Target ${position}`,
    targetImageAlt: (position: number): string => `Target ${position} image`,
    objectCount: (count: number): string => `${count} ${count === 1 ? 'object' : 'objects'}`,
    videoDetail: (size: string): string => `Video ${size}`,
    subtotal: 'Subtotal',
    gst: 'GST (18%)',
    total: 'Total payable',
    pricesNote: 'Prices are in INR and include 18% GST.',
    cancel: 'Cancel',
    pay: (amount: string): string => `Pay ${amount}`,
    lockAlt: 'Secure payment',
    payNote: 'Pay by UPI, card, net banking or wallet. Your GST invoice is emailed after payment.',
  },
  publishedModal: {
    successAlt: 'Published',
    title: 'Your project is live!',
    description: 'Anyone who scans your target image will now see your objects pop up.',
    shareLink: 'Share link',
    copy: 'Copy',
    backToProjects: 'Back to projects',
    downloadQr: 'Download QR code',
    seePreview: 'See the preview',
  },
  profile: {
    avatarAlt: 'Admin Profile Image',
  },
  upload: {
    fileTooLarge: 'File size is too large. Maximum size is',
    videoPriceNote: 'The price is for one target, and each target can have only one video.',
    videoPriceUpTo: 'Video up to',
  },
  project: {
    addTargets: 'Add target',
    rowActions: { edit: 'Edit', viewTracking: 'View tracking', manageExpiry: 'Manage expiry', downloadImages: 'Download images', delete: 'Delete' },
    status: { live: 'Live', ready: 'Ready to publish', needs: 'Needs a target' },
    subtitle: { live: 'Published', ready: 'Draft · not published', needs: 'No target image yet' },
    studioTagline: 'Your image-tracking AR projects, all in one place.',
    loadError: {
      title: "We can't reach your projects",
      message: "Your work is safe. We just couldn't load the list.",
      retry: 'Try again',
      iconAlt: 'Projects unavailable',
    },
    imageBasedAr: 'Image based AR',
    searchProjects: 'Search projects',
    sort: { label: 'Sort', labelWithValue: 'Sort:', menuHeading: 'Sort by' },
    filter: { ariaLabel: 'Filter projects by status', all: 'All' },
    columns: { project: 'Project', status: 'Status', targets: 'Targets', lastEdited: 'Last edited', actions: 'Actions' },
    createDialogTitle: 'Create a project',
    createDialogHint: 'Choose a name you’ll recognise. It can’t be changed after the project is created.',
    nameLabel: 'Project name',
    namePlaceholder: 'e.g. Spring menu poster',
    nameHelp: 'You’ll add the target image next.',
    duplicateName: (name: string): string =>
      `You already have a project called “${name}”. Try a different name.`,
    targetSingular: 'target',
    targetPlural: 'targets',
    publishSuccess: 'Published Successfully!',
    publishFailed: 'Published Failed',
    deleteDialog: {
      title: 'Delete project?',
      message: 'and its targets will be permanently deleted. This can’t be undone.',
      fallbackName: 'This project',
      confirm: 'Delete project',
      cancel: 'Cancel',
    },
  },
  signUp: {
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Confirm your password',
    passwordsDoNotMatch: 'Passwords do not match',
    termsRequired: 'Please accept the Terms & Conditions and Content Rules to continue',
    agreePrefix: 'I agree to the ',
    termsLinkText: 'Terms & Conditions',
    agreeJoiner: ' and ',
    contentRulesLinkText: 'Content Rules',
    termsUrl: 'https://picpopstudio.com/terms.html',
    contentRulesUrl: 'https://picpopstudio.com/content-rules.html',
  },
  googleAuth: {
    genericError: 'Something went wrong',
    // Must match the server's USE_GOOGLE_SIGN_IN message: the login form compares the error against it.
    googleOnlyAccount: 'This account signs in with Google. Please use the Google button.',
  },
};
